
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AdminClubSearch from '../../components/admin/AdminClubSearch';
import { loadAllClubList } from '../../modules/clubs/clubs';
import { changeClub } from '../../modules/clubs/club';
import { clubMemberSearch } from '../../lib/api/search';
import { getMemberPassword } from '../../lib/api/clubs';

const AdminSearchContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user, socketKey, clubs } = useSelector(({ user, socket, clubs }) => ({
    user: user.user,
    socketKey: socket.socketKey,
    clubs: clubs.clubs,
  }));

  const [keyword, setKeyword] = useState('');
  const [selectedClub, setSelectedClub] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [members, setMembers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (!clubs && user?.playerID && socketKey) {
      dispatch(
        loadAllClubList({
          playerID: Number(user.playerID),
          socketKey,
        }),
      );
    }
  }, [clubs, dispatch, socketKey, user]);

  useEffect(() => {
    if (!Array.isArray(clubs) || clubs.length === 0) {
      return;
    }
    const exists = clubs.some((club) => club.clubID === selectedClub);
    if (!exists && selectedClub !== 0) {
      setSelectedClub(clubs[0].clubID);
    }
  }, [clubs, selectedClub]);

  const clubOptions = useMemo(() => {
    const base = [{ label: '전체 클럽', value: 0 }];
    if (!Array.isArray(clubs)) {
      return base;
    }
    const mapped = clubs.map((club) => ({
      label: club.name,
      value: club.clubID,
    }));
    return base.concat(mapped);
  }, [clubs]);

  const handleKeywordChange = useCallback((event) => {
    setKeyword(event.target.value);
    setError(null);
  }, []);

  const selectedClubOption = useMemo(
    () => clubOptions.find((option) => option.value === selectedClub) || null,
    [clubOptions, selectedClub],
  );

  const resolveClubName = useCallback(
    (clubId) => {
      if (clubId === 0) return '-';
      if (!Array.isArray(clubs)) return '';
      const target = clubs.find((club) => club.clubID === clubId);
      return target?.name || '';
    },
    [clubs],
  );

  const runSearch = useCallback(
    async (
      pageNumber = 1,
      clubIdOverride = null,
    ) => {
      if (!socketKey) {
        setError('소켓 정보가 존재하지 않습니다.');
        return;
      }

      const clubId = clubIdOverride !== null ? clubIdOverride : selectedClub;

      if ((!Array.isArray(clubs) || clubs.length === 0) && clubId !== 0) {
        setError('조회 가능한 클럽이 없습니다.');
        return;
      }

      if (clubId === null || clubId === undefined) {
        setError('검색할 클럽을 선택해주세요.');
        return;
      }

      const trimmedKeyword = keyword.trim();
      const sanitizedKeyword = trimmedKeyword.replace(/['"]/g, '');

      setLoading(true);
      setError(null);
      setHasSearched(true);
      setMembers([]);
      setTotalCount(0);

      try {
        const { data } = await clubMemberSearch({
          clubID: clubId,
          keyword: sanitizedKeyword,
          page: pageNumber,
          limit,
          socketKey,
        });

        if (data?.result !== 1) {
          throw new Error('검색 결과를 불러오지 못했습니다.');
        }

        const computedTotal = typeof data.totalCount === 'number'
          ? data.totalCount
          : Array.isArray(data.infos)
            ? data.infos.length
            : 0;
        const computedTotalPages = Math.max(Math.ceil(computedTotal / limit), 1);

        if (pageNumber > computedTotalPages) {
          setPage(computedTotalPages);
          setLoading(false);
          await runSearch(computedTotalPages, clubId);
          return;
        }

        const infos = Array.isArray(data.infos)
          ? data.infos
              .filter((member, _, arr) => {
                if (member.clubID !== 0) {
                  return true;
                }
                return !arr.some(
                  (other) =>
                    other.playerID === member.playerID && other.clubID !== 0,
                );
              })
              .map((member) => ({
                ...member,
                clubName: member.clubName || resolveClubName(member.clubID),
              }))
          : [];

        setMembers(infos);
        setTotalCount(computedTotal);
      } catch (err) {
        console.error(err);
        setError('회원 정보를 불러오는 중 오류가 발생했습니다.');
        setMembers([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [clubs, keyword, limit, resolveClubName, selectedClub, socketKey],
  );

  const handleSearch = useCallback(() => {
    setPage(1);
    runSearch(1);
  }, [runSearch]);

  const handlePageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);
      runSearch(nextPage);
    },
    [runSearch],
  );

  const handleSelectClub = useCallback(
    (value) => {
      const numericValue = Number(value);
      setSelectedClub(numericValue);
      setPage(1);
      setError(null);
      setMembers([]);
      setTotalCount(0);
      if (hasSearched) {
        runSearch(1, numericValue);
      }
    },
    [hasSearched, runSearch],
  );

  const handleSelectMember = useCallback(
    (member) => {
      if (!member?.playerID) {
        return;
      }

      const targetClubID = selectedClub !== 0 ? selectedClub : member.clubID;

      if (!targetClubID || targetClubID === 0) {
        alert('클럽회원이 아닙니다.');
        return;
      }

      if (targetClubID) {
        try {
          sessionStorage.setItem('skipChangeClubRedirect', 'true');
        } catch (error) {
          console.warn('Failed to set skipChangeClubRedirect flag', error);
        }
        dispatch(changeClub({ clubID: targetClubID, socketKey }));
      }

      setTimeout(() => {
        history.push(`/member/@${member.playerID}?clubID=${targetClubID}`);
      }, 1000);
    },
    [dispatch, history, selectedClub, socketKey],
  );

  const handlePasswordCheck = useCallback(
    async (member) => {
      if (!socketKey || !member?.playerID) {
        alert('비밀번호를 확인할 수 없습니다.');
        return;
      }

      if (passwordLoading) {
        return;
      }

      try {
        setPasswordLoading(true);
        const { data } = await getMemberPassword({
          playerID: member.playerID,
          socketKey,
        });

        if (data?.result === 1) {
          alert(
            `${member.name || member.account || '회원'}님의 비밀번호는\n${data.password ?? '확인할 수 없습니다.'}`,
          );
        } else {
          alert('비밀번호를 확인할 수 없습니다.');
        }
      } catch (err) {
        console.error(err);
        alert('비밀번호 확인 중 오류가 발생했습니다.');
      } finally {
        setPasswordLoading(false);
      }
    },
    [passwordLoading, socketKey],
  );

    return (
    <AdminClubSearch
      keyword={keyword}
      onKeywordChange={handleKeywordChange}
      onSearch={handleSearch}
      loading={loading}
      members={members}
      totalCount={totalCount}
      page={page}
      limit={limit}
      clubOptions={clubOptions}
      selectedClub={selectedClub}
      onSelectClub={handleSelectClub}
      error={error}
      hasSearched={hasSearched}
      onPageChange={handlePageChange}
      clubName={selectedClubOption ? selectedClubOption.label : '전체 클럽'}
      onCheckPassword={handlePasswordCheck}
      passwordLoading={passwordLoading}
      onSelectMember={handleSelectMember}
    />
    );
};

export default AdminSearchContainer;
